/* ============================================================================
   Hot Cup Factory — quote builder → HubSpot
   ----------------------------------------------------------------------------
   Defines window.HCF_QUOTE_SUBMIT, the hook the builder calls at its SUBMIT
   SEAM. Everything HubSpot-specific lives here so that neither the builder
   (regenerated from the prototype) nor the adapter (Shopify catalogue mapping)
   has to know this exists.

   WHY THERE IS NO SERVER
   The handoff README assumed a server-side route, because a HubSpot private-app
   token must never reach the browser. True — so we never use one. HubSpot's
   Forms API has an unauthenticated submission endpoint built for exactly this:

     POST https://api.hsforms.com/submissions/v3/integration/submit/{portal}/{form}

   No token, no route, no infrastructure. The README's other constraint still
   holds — the Forms API cannot create a Deal — but the plan was never a Deal.
   It is a Contact, and the LEAD is created by the "Lead From Form" workflow,
   which enrols on FORM NAME. That is the whole reason we submit to a real form
   rather than writing a Contact through the CRM API: a CRM write creates no
   form submission, so the workflow never fires, and no BDR ever sees the
   request.

   THE INVISIBLE FAILURE
   If the form's name is not on that workflow's enrolment list you get a
   Contact, no Lead, a green thank-you screen, and every dashboard reporting
   success. Nothing here can detect that. It has to be verified once, by hand,
   with a real submission, before cutover.

   THE OUTBOX
   The builder fires this hook and immediately advances to the confirmation —
   it does not await us, and it calls clearState() straight after, so a failed
   POST would lose the request silently. Rather than edit the builder by hand
   (which would break the regenerate-from-prototype rule) we keep our own
   outbox: stash the payload before the request, clear it only on success, and
   retry whatever is left on the next page load. A dropped connection costs the
   customer nothing.
   ========================================================================== */
(function () {
  "use strict";

  var ROOT = document.querySelector("[data-quote-builder]");
  if (!ROOT) return;

  var PORTAL = (ROOT.getAttribute("data-hs-portal") || "").trim();
  var FORM = (ROOT.getAttribute("data-hs-form") || "").trim();
  var OUTBOX_KEY = "hcf-quote-outbox-v1";
  var FAILED_KEY = "hcf-quote-failed-v1";

  function warn(msg, err) {
    if (window.console && window.console.warn) {
      window.console.warn("[hcf-quote-builder] " + msg, err || "");
    }
  }

  /* Unconfigured is a normal state, not an error: the section ships before the
     HubSpot form exists. Say so once, clearly, and leave the builder working —
     the customer still gets the confirmation, and the payload is still on
     window for anyone debugging. */
  if (!PORTAL || !FORM) {
    warn("HubSpot portal ID or form ID is not set on the section — " +
         "quote requests are NOT being sent anywhere. Set both in the theme editor.");
    return;
  }

  var ENDPOINT = "https://api.hsforms.com/submissions/v3/integration/submit/" +
                 encodeURIComponent(PORTAL) + "/" + encodeURIComponent(FORM);

  /* --------------------------------------------------------------------------
     The readable spec.

     This is what a rep opens the record and reads, in `custom_quote_message` —
     the same property the existing custom-quote form writes to, so nobody has
     to learn a new place to look. Today that field holds things like "hot drink
     cup for 16 oz and 24 oz". This replaces that with something quotable.

     Plain text, no markup: HubSpot renders the property as plain text and any
     HTML would show as literal tags on the record.
     ------------------------------------------------------------------------ */
  function fmt(n) {
    return Number(n).toLocaleString("en-US");
  }

  function renderSpec(payload) {
    var out = [];
    out.push("Reference " + payload.ref);
    out.push("");

    (payload.lines || []).forEach(function (l) {
      var qty;
      if (l.quantityUnknown) {
        /* "Not sure yet" is a real answer and has to survive as one. Printing a
           number here would invent a commitment the customer never made. */
        qty = "quantity to be advised";
      } else if (l.quantities.length > 1) {
        /* Alternatives, never a sum. A rep who reads 5,000 + 10,000 as a
           15,000 unit order quotes the wrong thing. */
        qty = l.quantities.map(fmt).join(" or ") + "  (price each option)";
      } else {
        qty = fmt(l.quantities[0]);
      }

      var line = "• " + l.size + " " + l.product + " — " + qty;
      out.push(line);

      if (!l.quantityUnknown && l.unitsPerCase) {
        out.push("    " + l.cases.map(function (c) {
          return fmt(c) + " case" + (c === 1 ? "" : "s");
        }).join(" / ") + " of " + fmt(l.unitsPerCase));
      }
      if (l.alsoQuoteLids) {
        out.push("    + also quote matching lids");
      }
      if (l.leadTime) {
        out.push("    lead time " + l.leadTime + " from design approval");
      }
    });

    var a = payload.answers || {};
    out.push("");
    if (a.needBy)  out.push("Needed by:  " + a.needBy);
    if (a.artwork) out.push("Artwork:    " + a.artwork);
    if (a.useCase) out.push("Used for:   " + a.useCase);
    if (a.cadence) out.push("Order type: " + a.cadence);

    if (payload.note) {
      out.push("");
      out.push("Their note: " + payload.note);
    }

    return out.join("\n");
  }

  /* HubSpot wants firstname and lastname; the builder asks for one name,
     because asking a busy shop owner to split their own name is friction for
     our convenience. Split on the first space; a single word is a first name.
     "Mary Anne Smith" becomes Mary / Anne Smith, which is the right guess more
     often than the alternative. */
  function splitName(full) {
    var s = String(full || "").trim();
    var i = s.indexOf(" ");
    if (i === -1) return { first: s, last: "" };
    return { first: s.slice(0, i), last: s.slice(i + 1).trim() };
  }

  /* The HubSpot tracking cookie. Passing it links this submission to the
     visitor's real analytics session, so the Contact carries its original
     source instead of appearing out of nowhere. Absent when they block
     cookies, which is fine — the submission just loses its attribution. */
  function hutk() {
    var m = document.cookie.match(/(^|;)\s*hubspotutk\s*=\s*([^;]+)/);
    return m ? m[2] : "";
  }

  function buildFields(payload) {
    var name = splitName(payload.contact.name);
    var a = payload.answers || {};

    /* The form's Company Name field is REQUIRED and belongs to the COMPANY
       object, not the contact, so its field name is object-prefixed:
       `0-2/name` (0-2 is HubSpot's internal id for Company; contacts are 0-1).
       Omitting it 400s the whole submission with

         Error in 'fields.0-2/name'. Required field '0-2/name' is missing

       which is exactly what was happening — every quote request was being
       rejected. Verified against the live form by probing it with a
       deliberately invalid email, which validates every field without writing
       a record.

       We send BOTH this and the contact-level `company` property: the first
       creates and associates the Company record the form asks for, the second
       fills the contact field a rep sees on the contact card. They carry the
       same value and neither is redundant. */
    var business = String(payload.contact.business || "").trim();
    /* A required field must never be the reason a quote request is lost. The
       builder validates Business name as required so this should be
       unreachable — but if it ever is not, a Company named after the person
       who asked is recoverable and a dropped request is not. */
    if (!business) business = payload.contact.name || "Unknown business";

    var fields = [
      { name: "email",                value: payload.contact.email },
      { name: "firstname",            value: name.first },
      { name: "lastname",             value: name.last },
      { name: "0-2/name",             value: business },
      { name: "company",              value: business },
      { name: "phone",                value: payload.contact.phone },
      { name: "custom_quote_message", value: renderSpec(payload) },
      { name: "quote_reference",      value: payload.ref },
      { name: "quote_needed_by",      value: a.needBy },
      { name: "quote_artwork",        value: a.artwork },
      { name: "quote_use_case",       value: a.useCase },
      /* `quote_order_type`, not `quote_cadence`: the property was created with
         the label "Quote order type" and HubSpot derives the internal name from
         the label. Internal names are immutable after creation, so the code
         bends, not the property. Verified against the live property list. */
      { name: "quote_order_type",     value: a.cadence },
      /* The whole payload, verbatim. Everything above is a rendering; this is
         the source. If a rep ever needs a detail the spec did not print, or we
         later want to reprocess these, it is here. */
      { name: "quote_payload_json",   value: JSON.stringify(payload) }
    ];

    /* Empty optional answers are DROPPED, not sent blank. A dropdown property
       validates its value against its option list, and "" is not on any list —
       sending it would 400 the entire submission because somebody skipped an
       optional question. */
    return fields.filter(function (f) {
      return f.value !== undefined && f.value !== null && String(f.value) !== "";
    });
  }

  function submission(payload) {
    var context = {
      pageUri: window.location.href,
      pageName: document.title
    };

    /* hutk is OMITTED when absent, never sent blank. HubSpot rejects an empty
       string with INVALID_HUTK and 400s the entire submission — it does not
       simply ignore it. The cookie is missing more often than you would think:
       on a theme preview (no tracking script), for anyone blocking cookies, and
       on a first-ever visit that has not yet been stamped. Every one of those
       is a real customer whose quote request must still arrive. */
    var k = hutk();
    if (k) context.hutk = k;

    return {
      submittedAt: Date.now(),
      fields: buildFields(payload),
      context: context
    };
  }

  /* --------------------------------------------------------------------------
     Outbox
     ------------------------------------------------------------------------ */
  function readOutbox() {
    try {
      return JSON.parse(window.localStorage.getItem(OUTBOX_KEY)) || [];
    } catch (e) { return []; }
  }

  function writeOutbox(list) {
    try {
      if (list.length) window.localStorage.setItem(OUTBOX_KEY, JSON.stringify(list));
      else window.localStorage.removeItem(OUTBOX_KEY);
    } catch (e) { /* private window: we simply lose the retry, not the attempt */ }
  }

  function stash(body) {
    var list = readOutbox();
    list.push(body);
    /* A cap, because a persistently offline browser should not grow this
       without bound. Oldest goes first: a stale quote is worth less than the
       one they just built. */
    while (list.length > 5) list.shift();
    writeOutbox(list);
  }

  function unstash(body) {
    writeOutbox(readOutbox().filter(function (b) {
      return b.submittedAt !== body.submittedAt;
    }));
  }

  /* Rejected submissions, kept rather than dropped. Read them with:
       JSON.parse(localStorage.getItem("hcf-quote-failed-v1"))
     Nothing clears this automatically; it is a record, not a queue. */
  function shelve(body, status, detail) {
    try {
      var shelf = JSON.parse(window.localStorage.getItem(FAILED_KEY)) || [];
      shelf.push({ at: new Date().toISOString(), status: status, detail: detail, body: body });
      while (shelf.length > 10) shelf.shift();
      window.localStorage.setItem(FAILED_KEY, JSON.stringify(shelf));
    } catch (e) { /* nothing more we can do from here */ }
  }

  function post(body) {
    return window.fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (res.ok) return true;
      /* 400 means HubSpot rejected the shape — a missing form field, or a
         dropdown value that is not on the property's option list. Retrying
         cannot fix that, so drop it rather than retrying forever. Read the
         body: HubSpot names the offending field. */
      return res.text().then(function (t) {
        if (res.status >= 400 && res.status < 500) {
          /* Terminal: retrying cannot fix a form or property misconfiguration,
             so it leaves the retry queue. But it does NOT vanish — a rejected
             submission is still a customer who asked for a quote, and dropping
             it would lose them with nobody any the wiser. It moves to a
             separate shelf that nothing ever auto-clears, so the request can be
             recovered by hand once the configuration is fixed. */
          warn("HubSpot rejected the submission (" + res.status + "). " +
               "This is a form or property configuration problem, not a network one: " + t);
          shelve(body, res.status, t);
          return true;
        }
        throw new Error("HubSpot " + res.status + ": " + t);
      });
    });
  }

  function flush() {
    readOutbox().forEach(function (body) {
      post(body).then(function () { unstash(body); }, function (err) {
        warn("quote request still queued, will retry on next page load", err);
      });
    });
  }

  /* --------------------------------------------------------------------------
     The hook the builder calls.
     ------------------------------------------------------------------------ */
  window.HCF_QUOTE_SUBMIT = function (payload) {
    var body;
    try {
      body = submission(payload);
    } catch (e) {
      warn("could not build the HubSpot submission from the payload", e);
      return;
    }

    /* Stash BEFORE the request. The builder clears its own session the moment
       this returns, so between here and a response the request exists nowhere
       else. */
    stash(body);
    post(body).then(function () { unstash(body); }, function (err) {
      warn("quote request queued for retry", err);
    });
  };

  /* Anything left from a previous visit goes now. */
  flush();
})();
